import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Box, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { useSnackbar } from 'notistack';
import ListActionBar from 'src/components/ListActionBar';
import MissingValueText from 'src/components/MissingValueText';
import StatusButton from 'src/components/StatusButton';
import TableAppImage from 'src/components/Table/TableAppImage';
import TableContainer from 'src/components/Table/TableContainer';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import HighlightedText from 'src/components/HighlightedText';
import { TableCellTypography } from 'src/components/GenericTableCell';
import { useInfiniteAppPortfolioVersionList, useUpdateAppPortfolioStatus } from 'src/hooks/wdac';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { pagesText } from 'src/constants/pagesText';
import { formatDateShortLocal } from 'src/utils/formatTime';
import parseError from 'src/utils/parseError';
import {
  APPLICATION_TRUST_STATUS,
  getApplicationTrustStatusLabel,
} from 'src/utils/enums/applicationTrustStatus';
import { WDAC_APP_PREVIOUS_PATH } from 'src/pages/wdac-results/tabs/Win32Apps/constants';
import Win32AppStatusModal, {
  getStatusSavedMessage,
} from 'src/pages/wdac-results/tabs/Win32Apps/Win32AppStatusModal';
import { SourceChips } from './PortfolioTable';
import PortfolioTableHead from './PortfolioTableHead';
import {
  APP_PORTFOLIO_PAGE_SIZE,
  APP_PORTFOLIO_VERSION_COLUMNS,
  DEFAULT_PORTFOLIO_SORT,
  PORTFOLIO_FILTER_OPTIONS,
} from './constants';

const CLOSED_STATUS_MODAL = { open: false, version: null };

const ELLIPSIS_SX = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
  maxWidth: '100%',
};

const TEXT_SX = { fontSize: '0.72rem', m: 0, display: 'block', ...ELLIPSIS_SX };

// The applications view one version per row, so it reads like that view's expanded rows.
const PortfolioVersionsTable = ({ filters, onFilterChange, search, onSearchChange }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [sort, setSort] = useState(DEFAULT_PORTFOLIO_SORT);
  const [statusModal, setStatusModal] = useState(CLOSED_STATUS_MODAL);
  const trimmedSearch = search.trim();

  const versionsQuery = useInfiniteAppPortfolioVersionList({
    search: trimmedSearch,
    statuses: filters.statuses,
    sources: filters.sources,
    sort,
    limit: APP_PORTFOLIO_PAGE_SIZE,
  });

  const results = versionsQuery.data?.pages?.flatMap(page => page.results || []) || [];
  const totalCount = versionsQuery.data?.pages?.[0]?.totalCount || 0;

  const [sentryRef] = useInfiniteScroll({
    loading: versionsQuery.isFetchingNextPage,
    hasNextPage: !!versionsQuery.hasNextPage,
    onLoadMore: versionsQuery.fetchNextPage,
    rootMargin: '0px 0px 400px 0px',
  });

  const { mutateAsync: updateStatus, isLoading: statusLoading } = useUpdateAppPortfolioStatus();

  // The version is kept on close so the dialog body does not empty out mid transition.
  const closeStatusModal = () => setStatusModal(previous => ({ ...previous, open: false }));

  const statusAppId = statusModal.version?.globalApplicationVersionId;

  // A trust finishes on the general tab of the app, where what it built is shown.
  const saveStatus = async values => {
    try {
      await updateStatus({ globalApplicationVersionId: statusAppId, ...values });
      closeStatusModal();
      enqueueSnackbar(getStatusSavedMessage(values), {
        variant: 'success',
        autoHideDuration: 3000,
      });
      if (values.status === APPLICATION_TRUST_STATUS.Trusted) {
        openAppDetails(statusAppId);
      }
    } catch (error) {
      enqueueSnackbar(parseError(error) || pagesText.somethingWentWrong, {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const openAppDetails = globalApplicationVersionId => {
    sessionStorage.setItem(
      WDAC_APP_PREVIOUS_PATH,
      `${window.location.pathname}${window.location.search}`,
    );
    navigate(`${PORTAL_ROUTE}/wdac-apps/${globalApplicationVersionId}/general`);
  };

  const columnCount = APP_PORTFOLIO_VERSION_COLUMNS.length;

  return (
    <Box sx={{ position: 'relative' }}>
      <ListActionBar
        onSearch={onSearchChange}
        defaultSearch={search}
        onResetSearch={() => onSearchChange('')}
        searchPlaceholder="Search applications..."
        filters={filters}
        onFilterChange={onFilterChange}
        filterOptions={PORTFOLIO_FILTER_OPTIONS}
        filterChips
        filterButtonCount={totalCount}
      />

      <TableContainer roundedTop styles={{ maxHeight: `calc(100vh - 300px)` }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': { fontSize: '0.7rem' },
          }}>
          <PortfolioTableHead
            columns={APP_PORTFOLIO_VERSION_COLUMNS}
            sort={sort}
            onSortChange={setSort}
          />
          <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
            {versionsQuery.isLoading ? (
              <LoadingRows columnCount={columnCount} rowCount={10} />
            ) : (
              <>
                {results.map((version, index) => (
                  <TableRow
                    key={version.globalApplicationVersionId}
                    ref={index === results.length - 1 ? sentryRef : null}
                    hover
                    onClick={() => openAppDetails(version.globalApplicationVersionId)}
                    sx={{ cursor: 'pointer' }}>
                    <TableCell sx={{ py: 1 }}>
                      <TableAppImage
                        width={40}
                        height={40}
                        name={version.displayName}
                        imgSrc={version.iconUrl}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1, minWidth: 0 }}>
                      <TableCellTypography
                        sx={{ ...TEXT_SX, fontSize: '0.78rem', fontWeight: 600 }}>
                        <HighlightedText
                          text={version.displayName}
                          searchValue={trimmedSearch}
                          fallback={<MissingValueText label="name" />}
                        />
                      </TableCellTypography>
                    </TableCell>
                    <TableCell sx={{ py: 1, minWidth: 0 }}>
                      <TableCellTypography sx={TEXT_SX}>
                        <HighlightedText
                          text={version.publisher}
                          searchValue={trimmedSearch}
                          fallback={<MissingValueText label="publisher" />}
                        />
                      </TableCellTypography>
                    </TableCell>
                    <TableCell sx={{ py: 1, minWidth: 0 }}>
                      <TableCellTypography sx={TEXT_SX}>
                        {version.displayVersion || <MissingValueText label="version" />}
                      </TableCellTypography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <TableCellTypography sx={{ fontSize: '0.72rem', m: 0 }}>
                        {formatDateShortLocal(version.trustUpdatedOn) || '—'}
                      </TableCellTypography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <SourceChips sources={version.sources} />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <StatusButton
                        status={version.status}
                        size="small"
                        sx={{ fontSize: '10px', padding: '1px 6px', minWidth: 0 }}
                        onClick={event => {
                          event.stopPropagation();
                          setStatusModal({ open: true, version });
                        }}>
                        {getApplicationTrustStatusLabel(version.status)}
                      </StatusButton>
                    </TableCell>
                  </TableRow>
                ))}
                {versionsQuery.isFetchingNextPage && (
                  <LoadingRows columnCount={columnCount} rowCount={3} />
                )}
                {!versionsQuery.isFetchingNextPage && results.length === 0 && (
                  <NoDataCell colSpan={columnCount}>{pagesText.noDataFound}</NoDataCell>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Win32AppStatusModal
        open={statusModal.open}
        onClose={closeStatusModal}
        globalApplicationVersionId={statusAppId}
        summary={[statusModal.version?.displayName, statusModal.version?.displayVersion]
          .filter(Boolean)
          .join(' ')}
        currentStatus={statusModal.version?.status || ''}
        currentDate={statusModal.version?.trustUpdatedOn || null}
        onSave={saveStatus}
        loading={statusLoading}
      />
    </Box>
  );
};

export default PortfolioVersionsTable;
